import type { ReactNode } from 'react';

import { CountBadge } from './CountBadge';

export interface PreviewGridColumn<T> {
  header: string;
  width: number;
  /** Quantities and money go right, like every other numeric column in the product. */
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;

  /**
   * A TOTAL under the column it totals — rendered in a `<tfoot>` sticky to the bottom edge.
   *
   * Product call, 29 Sep 2026: *create a footer for the totals.* It replaces a
   * `<PreviewTable variant="table-display">` sitting BELOW the grid, which is the SALN
   * folder's shape and is right there — a filing's schedule totals one figure, under its own
   * rows, as a stated fact. It is wrong for a grid of several numeric columns: two label/value
   * rows under a seven-column table put `Total quantity 21` a long way from the Qty column it
   * is the sum of, and a reader has to match them up by name.
   *
   * **Only the columns that declare one get a cell**; the rest render empty, so a total always
   * sits under the figures it adds up.
   *
   * **READ THE SERVER'S FIGURE where there is one.** The signature hands over the rows for a
   * grid whose total is genuinely local, and a caller with a server-side sum closes over it
   * instead — a second arithmetic over the rows on screen is free to disagree with the one the
   * record states, and the rows on screen are a page of a collection in the general case.
   */
  footer?: (rows: T[]) => ReactNode;
}

/**
 * A record's CHILD COLLECTION, read-only, inside a preview.
 *
 * `<PreviewTable>` is the key/value half of this pair — one set of facts about one thing.
 * This is the grid half: a list of lines, read by scanning down a column. It is the rule the
 * 201 file already states one level up — "a section that is a HISTORY renders as a table, not
 * a card per row" — reaching the case where the history is nested INSIDE one record rather
 * than hanging off the profile.
 *
 * **It exists because a column cannot express a collection.** A row preview is built from the
 * table's own columns, and the most a column can say about a list of twelve declared assets is
 * `12`. The count is the honest summary in a grid of filings; it is not what somebody opening
 * one filing came to read.
 *
 * **Read-only, and not a `<DataTable>`.** No search, no sort, no paging, no resize, no
 * selection, no kebab — a preview writes nothing and the collection arrives whole, so every
 * one of those would be chrome over a dozen rows that are all already on screen. What it DOES
 * take from the two table shells is the grid itself, class for class: the `bg-slate-100`
 * header band at 9px uppercase with the weight on the `<th>` cells, `border-l` column rules,
 * `border-t` row rules, and `align-middle`. A third table treatment in one product is the
 * drift the shared shells exist to prevent.
 *
 * **It scrolls inside its own container.** A schedule of seven columns is wider than a modal,
 * and the page body never scrolls horizontally.
 */
export function PreviewGrid<T>({
  title,
  columns,
  rows,
  rowKey,
  fill = false,
  empty,
}: {
  /**
   * Optional, because the heading is not always this component's to draw: inside a folder the
   * TAB names the collection and carries its count, so a heading here would be the same value
   * in two slots one line apart.
   */
  title?: string;
  columns: PreviewGridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** What to say when the collection is empty — never a blank panel, which reads as a fault. */
  empty: string;

  /**
   * Takes the WHOLE height it is given, anchored on all four sides, and scrolls inside itself.
   *
   * Product call, 29 Sep 2026: *apply full width table, anchored from top to bottom and all
   * sides.* It is `<DataTablePanel fill>`'s own prop and its own reason, one shell along: a
   * grid sized by its content leaves a two-row collection floating at the top of a screen of
   * white, and the reader cannot tell whether the list ended or the page failed to finish.
   *
   * **The CALLER has to offer the height.** `fill` distributes what it is given, so the tab's
   * own container is `flex h-full min-w-0 flex-col` and the profile shell is in its `bleed`
   * panel mode — a `cards` panel is capped at a readable measure and has no height to hand
   * over.
   *
   * Off by default: a grid inside a `<ProfileCard>` or a modal is sized by its content, and
   * stretching one to the viewport would put two rows in a box the height of the screen.
   */
  fill?: boolean;
}) {
  /** One column declaring a total turns the footer — and the stretch below — on. */
  const hasFooter = columns.some((column) => column.footer);

  return (
    <section
      className={`flex min-w-0 flex-col gap-1.5 ${fill ? 'h-full min-h-0' : ''}`}
    >
      {/*
        The count rides INSIDE the heading through `<CountBadge>`, the way a folder tab carries
        one — and a zero renders, because "nothing was declared here" is the answer a reader of
        a statutory filing is most often checking for.
      */}
      {title ? (
        <h3 className="flex items-center gap-1.5 text-xs font-medium text-slate-800">
          {title} <CountBadge count={rows.length} />
        </h3>
      ) : null}

      {rows.length === 0 ? (
        <p className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500">
          {empty}
        </p>
      ) : (
        /*
          CLIPPED ON BOTH AXES beside the radius, which is what both sibling surfaces already do
          — `<PreviewTable>` and `<IndexPage>`'s own panel. Without it the header band's
          `bg-slate-100` paints a square corner over the rounded border, so the grid reads as
          having a broken edge at the top two corners while every other panel is clean.

          <b>PER AXIS, never `overflow-hidden` beside `overflow-x-auto`.</b> Those are two
          declarations of one shorthand at equal specificity, so which wins is Tailwind's
          stylesheet order rather than anything this file states — a scroll that works today and
          silently stops after a utility is added elsewhere. The same trap the banded table's
          border tones document.
        */
        <div
          className={`modern-scrollbar min-w-0 rounded-lg border border-slate-200 ${
            fill
              ? // FILLING: it absorbs the slack and scrolls on BOTH axes inside itself, which is
                // what anchors the grid to all four sides rather than to its own content.
                'min-h-0 flex-1 overflow-auto'
              : 'overflow-x-auto overflow-y-hidden'
          }`}
        >
          {/*
            FIXED layout, so the widths a caller declares are the widths that are drawn.

            Under the default `auto` layout `w-full` distributes the table's width by CONTENT,
            and every `width` on a `<th>` is advisory — so a long reason ate the slack and every
            divider landed somewhere the column definitions never asked for. `minWidth:
            max-content` still lets a grid wider than its container scroll rather than crushing
            its columns, which is the behaviour this component's own remarks promise.
          */}
          {/*
            STRETCHED ONLY WHERE A FOOTER HAS TO SIT ON THE BOTTOM EDGE, and only a `fill` grid
            has an edge to sit on — `sticky bottom-0` pins nothing until its container actually
            scrolls, so a short filled grid left its total floating mid-panel. The spacer row at
            the end of the tbody takes the slack, or `height: 100%` would hand it to the ROW
            boxes and render three rows at a third of the panel each.
          */}
          <table
            className="w-full border-collapse"
            style={{
              tableLayout: 'fixed',
              minWidth: 'max-content',
              height: fill && hasFooter ? '100%' : undefined,
            }}
          >
            <thead>
              {/*
                The weight is on the CELLS, never on the row: a `<th>` carries the browser's own
                bold and Tailwind's preflight does not reset it, so `font-normal` set on the
                `<tr>` would never arrive. The same trap both table shells document.
              */}
              <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                {columns.map((column) => (
                  <th
                    key={column.header}
                    scope="col"
                    style={{ width: column.width }}
                    className={`border-l border-slate-200 px-3 py-2 first:border-l-0 ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} className="border-t border-slate-100">
                  {columns.map((column) => (
                    <td
                      key={column.header}
                      /*
                        THE TONE IS THE SHELL'S, exactly as `<TextCell>` resolves it for the two
                        table shells. With none set the cell INHERITS `body`'s `#0f172a` — the
                        selection tone — so every value in every caller's grid painted darker
                        than the heading above it, and read as deliberate because all of them
                        agreed. A tone a dozen renderers have to remember is one they will forget.
                      */
                      className={`border-l border-slate-100 px-3 py-2 align-middle text-slate-500 first:border-l-0 ${
                        column.align === 'right' ? 'text-right' : ''
                      }`}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}

              {/*
                Absorbs the stretch, so the rows above keep the height they asked for.

                ONE CELL PER COLUMN, never a `colSpan` — the grid has to run to the footer. A
                single spanning cell draws no vertical rules, so the column lines stopped under
                the last row and the band above the totals was blank.
              */}
              {fill && hasFooter ? (
                <tr aria-hidden="true" className="h-full">
                  {columns.map((column) => (
                    <td
                      key={column.header}
                      className="border-l border-slate-100 p-0 first:border-l-0"
                    />
                  ))}
                </tr>
              ) : null}
            </tbody>

            {/*
              THE TOTALS, UNDER THE COLUMNS THEY TOTAL — rendered only when a column declares
              one, so a grid with no footer is byte-for-byte what it was.

              REGULAR WEIGHT, and it is NOT bold — product call, 29 Sep 2026, in the reader's
              own words: *do not bold the font.* `font-semibold` was here and it genuinely
              applied (a `<td>` carries no user-agent bold, unlike the `<th>` both table shells
              document), which is exactly why dropping it was a real change rather than the
              removal of an inert class.

              WHAT SEPARATES IT is the `border-t` and the `bg-slate-50` tint, which is the
              table-display label column's own argument: the tint IS the treatment, and a total
              that also shouted was the heaviest text on a grid whose content is the figures
              above it.

              STICKY to the bottom edge BY DEFAULT — never opt-in. A total is what the rows add
              up to, so a filling grid that scrolled it away would hide the one figure the grid
              is read for. All three tables in the product agree on this.
            */}
            {hasFooter ? (
              <tfoot className="sticky bottom-0 z-10">
                <tr className="border-t border-slate-200 bg-slate-50 text-sm text-slate-600">
                  {columns.map((column) => (
                    <td
                      key={column.header}
                      className={`border-l border-slate-200 px-3 py-2 align-middle first:border-l-0 ${
                        column.align === 'right' ? 'text-right' : ''
                      }`}
                    >
                      {column.footer ? column.footer(rows) : null}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </section>
  );
}
